import { Controller } from "@hotwired/stimulus"
import {
  normalizeText, rowMatches, passesFilter, rowIsActive, groupLabel, mixLabel, footerLabel, sortedPartitions
} from "../modules/products_catalog.mjs"

// Mis productos: búsqueda y filtro sobre las filas del servidor; el resto se deriva del DOM y un
// MutationObserver lo vuelve a aplicar. Productos archivados usa el mismo controller.
// Doc: _docs/features/products/partner_products_index.md
export default class extends Controller {
  static targets = [
    "list", "search", "clear", "filterButton", "countAll", "countOn", "countOff",
    "section", "sectionCount", "sectionMix", "row", "noResults", "queryEcho",
    "footer", "empty", "navItem"
  ]
  static values = { delay: { type: Number, default: 150 } }

  connect() {
    // Turbo restaura el DOM al volver atrás: el estado parte de lo que hay en pantalla
    this.query = normalizeText(this.hasSearchTarget ? this.searchTarget.value : "")
    this.status = this.filterButtonTargets.find((button) => button.getAttribute("aria-pressed") === "true")?.dataset.status || "all"
    this.openBeforeSearch = null
    if (this.hasListTarget) {
      this.observer = new MutationObserver(() => this.scheduleApply())
      this.observer.observe(this.listTarget, {
        childList: true, subtree: true, attributes: true, attributeFilter: ["data-active"]
      })
    }
    this.apply()
    this.#watchSections()
  }

  disconnect() {
    this.observer?.disconnect()
    this.spy?.disconnect()
    clearTimeout(this.timeout)
    clearTimeout(this.applyTimeout)
  }

  search() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.setQuery(this.searchTarget.value), this.delayValue)
  }

  clear() {
    clearTimeout(this.timeout)
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
    this.setQuery("")
  }

  filter(event) {
    this.status = event.currentTarget.dataset.status
    this.filterButtonTargets.forEach((button) => {
      button.setAttribute("aria-pressed", String(button === event.currentTarget))
    })
    this.apply()
  }

  toggleSection(event) {
    const section = event.currentTarget.closest(".catalog-section")
    this.#setOpen(section, !section.classList.contains("is-open"))
  }

  toggleGroup(event) {
    event.currentTarget.closest(".catalog-section").classList.toggle("is-group-open")
    this.apply()
  }

  // Navegador de secciones (desktop): abre la sección y la lleva a la vista.
  jump(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.sectionId
    const section = this.sectionTargets.find((candidate) => candidate.dataset.sectionId === id)
    if (!section) return
    this.#setOpen(section, true)
    this.#setActiveNav(id)
    section.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Al buscar se abren secciones y grupos con resultados; al limpiar vuelve lo que estaba abierto
  setQuery(value) {
    const next = normalizeText(value)
    if (next === this.query) {
      this.apply()
      return
    }
    const wasSearching = this.query !== ""
    this.query = next
    if (!wasSearching && next) {
      this.openBeforeSearch = {
        sections: this.sectionTargets.filter((section) => section.classList.contains("is-open")),
        groups: this.sectionTargets.filter((section) => section.classList.contains("is-group-open"))
      }
    }
    if (wasSearching && !next && this.openBeforeSearch) {
      this.sectionTargets.forEach((section) => {
        this.#setOpen(section, this.openBeforeSearch.sections.includes(section))
        section.classList.toggle("is-group-open", this.openBeforeSearch.groups.includes(section))
      })
      this.openBeforeSearch = null
    }
    this.apply({ queryChanged: true })
  }

  // Varias mutaciones seguidas se aplican una vez; setTimeout y no rAF (pestaña oculta)
  scheduleApply() {
    clearTimeout(this.applyTimeout)
    this.applyTimeout = setTimeout(() => this.apply(), 0)
  }

  apply({ queryChanged = false } = {}) {
    const query = this.query
    const status = this.status
    let totalAll = 0
    let totalOn = 0
    let shown = 0
    let sectionsShown = 0

    this.sectionTargets.forEach((section) => {
      const sectionHit = query !== "" && (section.dataset.sectionName || "").includes(query)
      const body = section.querySelector(".catalog-section__body")
      const group = section.querySelector(".inactive-group")
      const rows = Array.from(section.querySelectorAll(".product-row")).map((el) => ({
        el,
        active: rowIsActive(el.dataset.active),
        order: Number(el.dataset.order) || 0,
        matches: sectionHit || rowMatches(el.dataset.name, query)
      }))

      // Activas, grupo, inactivas: una fila que cambió con el switch cae en su lugar
      const { active, inactive } = sortedPartitions(rows)
      this.#reorder(body, [...active.map((row) => row.el), group, ...inactive.map((row) => row.el)])

      const inactiveMatches = inactive.filter((row) => row.matches)
      if (queryChanged && query !== "" && inactiveMatches.length > 0) section.classList.add("is-group-open")
      const groupOpen = section.classList.contains("is-group-open")
      let visible = 0

      rows.forEach((row) => {
        const passes = row.matches && passesFilter(row.active, status)
        row.el.hidden = !passes
        // Con "Todos", en mobile los inactivos viven detrás del botón del grupo
        row.el.classList.toggle("is-grouped", !row.active && status === "all")
        if (passes) visible++
      })

      totalAll += rows.length
      totalOn += active.length

      if (group) {
        group.hidden = !(status === "all" && inactiveMatches.length > 0)
        group.setAttribute("aria-expanded", String(groupOpen))
        this.#setText(group.querySelector(".inactive-group__label"), groupLabel(inactiveMatches.length, groupOpen))
        this.#refreshPreview(group, inactiveMatches.slice(0, 3).map((row) => row.el))
      }
      this.#setText(section.querySelector(".catalog-section__count"), String(rows.length))
      this.#setText(section.querySelector(".catalog-section__mix"), mixLabel(active.length, inactive.length))

      section.hidden = visible === 0
      if (queryChanged && query !== "" && visible > 0) this.#setOpen(section, true)
      if (visible > 0) {
        shown += visible
        sectionsShown++
      }

      const navItem = this.navItemTargets.find((item) => item.dataset.sectionId === section.dataset.sectionId)
      if (navItem) {
        navItem.hidden = visible === 0
        this.#setText(navItem.querySelector(".section-nav__count"), String(rows.length))
      }
    })

    // El stream de archivar quita la sección que quedó vacía: su enlace del navegador también se va
    const liveSections = new Set(this.sectionTargets.map((section) => section.dataset.sectionId))
    this.navItemTargets.forEach((item) => {
      if (!liveSections.has(item.dataset.sectionId)) item.hidden = true
    })

    if (this.hasCountAllTarget) this.#setText(this.countAllTarget, String(totalAll))
    if (this.hasCountOnTarget) this.#setText(this.countOnTarget, String(totalOn))
    if (this.hasCountOffTarget) this.#setText(this.countOffTarget, String(totalAll - totalOn))

    const rawQuery = this.hasSearchTarget ? this.searchTarget.value : ""
    if (this.hasClearTarget) this.clearTarget.hidden = rawQuery === ""
    if (this.hasQueryEchoTarget) this.#setText(this.queryEchoTarget, rawQuery.trim())
    if (this.hasNoResultsTarget) this.noResultsTarget.hidden = !(query !== "" && shown === 0 && totalAll > 0)
    if (this.hasFooterTarget) this.#setText(this.footerTarget, footerLabel(shown, sectionsShown))

    const empty = totalAll === 0
    this.element.classList.toggle("is-empty", empty)
    if (this.hasEmptyTarget) this.emptyTarget.hidden = !empty
  }

  // Navegador (desktop): marca la sección que cruza la banda superior
  #watchSections() {
    if (this.navItemTargets.length === 0 || !("IntersectionObserver" in window)) return
    const crossing = new Set()
    this.spy = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.isIntersecting ? crossing.add(entry.target) : crossing.delete(entry.target))
      const first = this.sectionTargets.find((section) => crossing.has(section))
      if (first) this.#setActiveNav(first.dataset.sectionId)
    }, { rootMargin: "-76px 0px -60% 0px" })
    this.sectionTargets.forEach((section) => this.spy.observe(section))
  }

  #setActiveNav(id) {
    this.navItemTargets.forEach((item) => item.classList.toggle("is-active", item.dataset.sectionId === id))
  }

  #setOpen(section, open) {
    section.classList.toggle("is-open", open)
    section.querySelector(".catalog-section__header")?.setAttribute("aria-expanded", String(open))
  }

  // Mueve nodos solo si el orden cambió (cada movimiento es una mutación)
  #reorder(body, desired) {
    const wanted = desired.filter(Boolean)
    const current = Array.from(body.children).filter((el) => wanted.includes(el))
    if (wanted.every((el, index) => el === current[index])) return
    wanted.forEach((el) => body.appendChild(el))
  }

  // Miniaturas del grupo = sus primeras filas inactivas; data-preview evita repintar igual
  #refreshPreview(group, rowElements) {
    const thumbs = group.querySelector(".inactive-group__thumbs")
    if (!thumbs) return
    const signature = rowElements.map((el) => el.id).join(",")
    if (thumbs.dataset.preview === signature) return
    thumbs.dataset.preview = signature
    thumbs.replaceChildren(...rowElements.map((el) => {
      const source = el.querySelector(".product-row__thumb")
      const thumb = document.createElement("span")
      thumb.className = "inactive-group__thumb"
      thumb.style.cssText = source?.style.cssText || ""
      thumb.innerHTML = source?.innerHTML || ""
      return thumb
    }))
  }

  // Solo escribe si cambió (el observer mira la lista)
  #setText(element, value) {
    if (element && element.textContent !== value) element.textContent = value
  }
}
